import React, {useCallback, useEffect, useState} from 'react';
import {Col, Container, Row} from 'reactstrap';
import classNames from 'classnames';
import {Location} from '@reach/router';
import queryString from 'query-string';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faArrowDown} from '@fortawesome/pro-regular-svg-icons';
import {subscribeToCampaign} from 'react-controlled-ab/datalayers/vwo';

import {usePhaseState} from 'funnel-schedule/phase-context';
import useBalanceText from 'utils/useBalanceText';

import PageMeta from 'components/PageMeta';
import Header from 'components/Elements/Header';
import Section, {SectionImage} from 'components/Elements/Section';
import CountdownTimer from 'components/Elements/CountdownTimer';
import Video from 'components/Video';
import IconList, {IconListItem} from 'components/Elements/IconList';
import OptinModal, {OptinModalTrigger} from 'components/Modals/OptinModal';
import ModalExitLanding from 'components/Modals/ModalExitLanding';
import ShareButtons from 'components/Social/ShareButtons';
import Footer from 'components/Elements/Footer';

export default function Index(props) {
  return (
    <Location>
      {({location, navigate}) => <MasterClassIndex location={location} navigate={navigate} {...props} />}
    </Location>
  );
}
function MasterClassIndex({
  location = {},
  entry = 'health',
  noShare = false,
  noFixedVideo = false,
  noExitIntent = false,
  slug = 'masterclass',
  navigate,
  variant = null,
  pageTitle = 'How to Feel Awesome with a Plant-based Diet',
  pageDescription = 'Watch this free mini-masterclass to learn what to eat to feel good again.',
  ogImage = 'https://cdn.foodrevolution.org/ppp/og/ppp-opengraph-health-1200x1200-v20221103.jpg',
  shareUrl = 'https://plantplaybook.foodrevolution.org/masterclass/health/',
  facebookDescription = 'Feeling less healthy than you’d like to be? A plant-based diet has helped millions of people feel good again. Watch this free mini-masterclass for 3 of the top questions — and answers — about a plant-based diet:',
  twitterDescription = 'Feeling less healthy than you’d like to be? A plant-based diet has helped millions of people feel good again. Watch this mini-masterclass, How a Plant-Based Diet Can Make You Feel Better — airing now for free.',
  twitterImg = 'https://cdn.foodrevolution.org/ppp/og/ppp-opengraph-health-1200x630-20221103.jpg',
  pinterestDescription = 'Feeling less healthy than you’d like to be? A plant-based diet has helped millions of people feel good again. Watch this mini-masterclass, How a Plant-Based Diet Can Make You Feel Better — airing now for free.',
  pinterestImage = 'https://cdn.foodrevolution.org/ppp/og/ppp-opengraph-health-1000x1500-20221103.jpg',
  ...props
}) {
  const qs = typeof window !== 'undefined' ? queryString.parse(window.location.search) : {};
  const navigateWatch = useCallback(() => navigate('/masterclass/watch/'), []);
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const pathName = window.location.pathname;
      const pathRegex = new RegExp(`\/${slug}\/`, 'g');
      if (!pathName.match(pathRegex)) {
        navigate(`/${slug}/` + location.search);
      }
    }
  }, []);

  const [groupOneControl, setGroupOneControl] = useState(props.groupOneControl || 'control');

  useEffect(() => {
    subscribeToCampaign((variantIndex) => {
      switch (variantIndex) {
        case 1:
          setGroupOneControl('v1');
          break;
        case 2:
          setGroupOneControl('v2');
          break;
      }
    }, 691);
  }, []);

  const {
    phases: {
      default: defaultSchedule = {
        phase: 'before',
        launchEnd: ''
      }
    }
  } = usePhaseState();

  const launchEnd = new Date(defaultSchedule.launchEnd).getTime();

  function OptinButton({className = null, ...props}) {
    return (
      <OptinModalTrigger className={className}>
        Reserve Your <i>Free</i> Spot Now
      </OptinModalTrigger>
    );
  }

  function LandingTimer({className = null, ...props}) {
    return (
      <div className={classNames(`section-countdown countdown-${props.light ? 'light' : 'dark'}`, className)}>
        <h5 className="timer-heading mt-0 mb-2">{defaultSchedule.launchTitle} ends in:</h5>
        <CountdownTimer time={launchEnd} />
      </div>
    );
  }

  useBalanceText();
  return (
    <div className="page d-flex flex-column">
      <PageMeta title={pageTitle} description={pageDescription} url={shareUrl} ogImage={ogImage} />
      <div className="page-content">
        <Header
          className="background-purple"
          logoWhite
          style="dark"
          shareUrl={shareUrl}
          shareImg={ogImage}
          facebookDescription={facebookDescription}
          twitterDescription={twitterDescription}
          twitterImg={twitterImg}
          pinterestDescription={pinterestDescription}
          pinterestImage={pinterestImage}
        />

        <Section
          id="landing-header-cta"
          className={classNames(`section-angle-first landing-header-cta landing-header-cta-${entry}`)}
          color="black"
          innerClass="text-white text-center"
          angle="none"
        >
          <Container>
            <Row className={classNames(`d-flex justify-content-center justify-content-lg-end align-items-center`)}>
              <Col xs="12" lg="7">
                <p className="balance-text">
                  <i>FREE Mini-masterclass Now Playing For A Limited Time</i>
                </p>
                {
                  {
                    curious: (
                      <>
                        {variant === 'ad' ? (
                          <>
                            {
                              {
                                control: (
                                  <h2 className="mt-0">
                                    Why is everyone going plant-based all of a sudden? (And why you should think about
                                    joining them!)
                                  </h2>
                                ),
                                v1: (
                                  <>
                                    <h2 className="mt-0 mb-3">Top Reasons To Go Plant-Based</h2>
                                    <h3 className="mt-0">(And How To Do It Right!)</h3>
                                  </>
                                ),
                                v2: (
                                  <>
                                    <h2 className="mt-0 mb-3">Top Reasons To Go Plant-Based</h2>
                                    <h3 className="mt-0">(And The Mistakes To Avoid!)</h3>
                                  </>
                                )
                              }[groupOneControl]
                            }
                          </>
                        ) : (
                          <h2 className="mt-0">
                            Why is everyone going plant-based all of a sudden? (And why you should think about joining
                            them!)
                          </h2>
                        )}
                      </>
                    ),
                    sustainability: <h2 className="mt-0">How your food choices can help save the planet (for real)</h2>,
                    health: <h2 className="mt-0">How a plant-based diet can make you feel awesome</h2>
                  }[entry]
                }

                <div className="d-lg-none">
                  <Row className="d-flex justify-content-center my-4">
                    <Col xs="8" sm="7">
                      <img
                        className="img-fluid border-solid border-white border-5 rounded-circle"
                        src={
                          {
                            curious: require('static/backgrounds/871227828-square.jpg'),
                            sustainability: require('static/backgrounds/1035544898.jpg'),
                            health: require('static/backgrounds/522319544-square.jpg')
                          }[entry]
                        }
                      />
                    </Col>
                  </Row>
                </div>
                {variant === 'ad' ? null : (
                  <>
                    {defaultSchedule.phase === 'during' ? (
                      <LandingTimer className="countdown-small mb-4" light />
                    ) : null}
                  </>
                )}
                <OptinButton className="mt-4" />
              </Col>
            </Row>
          </Container>
        </Section>

        <Section id="landing-video" color="light-gray" className="text-center" angle="both">
          <Container>
            <Row className="d-flex align-items-center justify-content-center">
              <Col xs="12" lg="10" className="video-col px-0 px-sm-3">
                <div className="px-3 px-sm-0 mb-3">
                  <p className="mb-0">
                    <FontAwesomeIcon icon={faArrowDown} /> Watch the Trailer <FontAwesomeIcon icon={faArrowDown} />
                  </p>
                </div>
                <div>
                  <Video
                    url={
                      {
                        curious: 'https://www.youtube.com/watch?v=eR966GmT2Is',
                        sustainability: 'https://www.youtube.com/watch?v=cGyMxHDL1AM',
                        health: 'https://www.youtube.com/watch?v=8ssVdtPDlb0'
                      }[entry]
                    }
                    label={
                      {
                        curious: 'PPP - Masterclass Trailer - Veg Curious',
                        sustainability: 'PPP - Masterclass Trailer - Sustainability Minded',
                        health: 'PPP - Masterclass Trailer - Health Motivated'
                      }[entry]
                    }
                  />
                </div>
              </Col>
            </Row>
          </Container>
        </Section>

        <Section id="landing-message" color="black" imgUrl={require('static/backgrounds/wood-panel.jpg')} angle="none">
          <Container>
            <Row>
              <Col>
                <div className="background-white box-shadow-black-50 p-4 p-lg-5 rounded">
                  <Row className="align-items-center mb-4">
                    <Col xs="10" sm="8" lg="3" className="order-lg-2 mb-4 mb-lg-0">
                      <img
                        className="rounded-circle"
                        src="https://cdn.foodrevolution.org/global/team/ocean-robbins-headshot.jpg"
                        alt="Ocean Robbins headshot"
                      />
                    </Col>
                    <Col xs="12" lg="9" className="order-lg-1">
                      <h2 className="section-only-heading">
                        An Urgent Message From Ocean Robbins, Co-Founder of Food Revolution Network
                      </h2>
                    </Col>
                  </Row>
                  <p>Hi, I’m Ocean.</p>
                  {
                    {
                      curious: (
                        <>
                          <p>
                            {variant === 'ad' ? (
                              <>
                                Want to feel incredible, make a difference for climate change, and be kind to animals —
                                while eating delicious food? This Mini-Masterclass will show you how.
                              </>
                            ) : (
                              <>
                                Want to finally find the health of your dreams, make a difference for climate change,
                                and be kind to animals — while eating delicious food? This Mini-Masterclass will show
                                you how.
                              </>
                            )}
                          </p>
                          <p>
                            <b>But here’s the deal:</b> It can’t wait. Every meal matters if you want to make a
                            difference.
                          </p>
                          <p>So, I’m going to share how to start getting wins right away.</p>
                          <p>
                            After decades of inspiring millions of people around the world to eat food that gives them
                            energy and makes them healthy, I can confidently say that when{' '}
                            {variant === 'ad' ? 'we' : 'you'} eat the right plant-based foods, the rewards can stack up.
                          </p>
                          <p>Not just for {variant === 'ad' ? 'us' : 'you'} but for the planet, too.</p>
                          <p>
                            {variant === 'ad' ? (
                              <>
                                If you’re willing to invest just 15 minutes to learn what the latest research tells us
                                about the foods that have been proven to create lasting health — and about the foods
                                that can help save our planet, then I promise you’re going to be light-years ahead of
                                where you were when you woke up this morning.
                              </>
                            ) : (
                              <>
                                If you’re willing to invest just 15 minutes to learn what the latest research tells us
                                about the foods that have been proven to help fight heart disease, cancer, Alzheimer’s,
                                Type 2 diabetes, and chronic inflammation — and about the foods that can help save our
                                planet, then I promise you’re going to be light-years ahead of where you were when you
                                woke up this morning.
                              </>
                            )}
                          </p>
                          <p>
                            {variant === 'ad' ? (
                              <>
                                My goal? After this free Mini-Masterclass, you’ll know what to do and how to do it. It’s
                                time to create a life that’s fueled by health, abundant energy, and mental clarity. And
                                to feel good, knowing how to be part of the solution on planet Earth.
                              </>
                            ) : (
                              <>
                                My goal? After this free Mini-Masterclass, you’ll know what to do and how to do it.
                                You’ll be ready to step into a life that’s fueled by health, abundant energy, and mental
                                clarity. And you’ll feel so good, knowing how to be part of the solution on planet
                                Earth.
                              </>
                            )}
                          </p>
                        </>
                      ),
                      sustainability: (
                        <>
                          <p>
                            {variant === 'ad' ? (
                              <>
                                Want to make a difference for climate change, be kind to animals, and experience
                                legendary health — while eating delicious food? This Mini-Masterclass will show you how.
                              </>
                            ) : (
                              <>
                                Want to make a difference in climate change, be kind to animals, and know you’re doing
                                the best thing for your health — while eating delicious food? This Mini-Masterclass will
                                show you how.
                              </>
                            )}
                          </p>
                          <p>
                            <b>But here’s the deal:</b> It can’t wait. Every meal matters if you want to make a
                            difference.
                          </p>
                          <p>So, I’m going to share how to start seeing wins right away.</p>
                          <p>
                            Small actions, especially what {variant === 'ad' ? 'we' : 'you'} eat, can make a BIG
                            difference.
                          </p>
                          <p>
                            {variant === 'ad' ? (
                              <>
                                If you’re willing to invest just 15 minutes to learn what the latest research tells us
                                about the HUGE impact food has on our planet and how a climate-friendly diet can be
                                super healthy and super tasty, then I promise you’re going to be light-years ahead of
                                where you were when you woke up this morning.
                              </>
                            ) : (
                              <>
                                If you’re willing to invest just 15 minutes to learn what the latest research tells us
                                about the HUGE impact food has on our planet, and about how a climate-friendly diet can
                                be super healthy and super tasty, then I promise you’re going to be light-years ahead of
                                where you were when you woke up this morning.
                              </>
                            )}
                          </p>
                          <p>
                            {variant === 'ad' ? (
                              <>
                                My goal? After this free Mini-Masterclass, you’ll know what to do and how to do it.
                                Ready to step into a life that’s fueled by health, abundant energy, and mental clarity?
                                And to feel good, knowing exactly how to be part of the solution on planet Earth?
                              </>
                            ) : (
                              <>
                                My goal? After this free Mini-Masterclass, you’ll know what to do and how to do it.
                                You’ll be ready to step into a life that’s fueled by health, abundant energy, and mental
                                clarity. And you’ll feel so good, knowing exactly how to be part of the solution on
                                planet Earth.
                              </>
                            )}
                          </p>
                        </>
                      ),
                      health: (
                        <>
                          <p>
                            {variant === 'ad' ? (
                              <>
                                Want to feel incredible while eating delicious food? This Mini-Masterclass will show you
                                how.{' '}
                              </>
                            ) : (
                              <>
                                Want to finally find the health of your dreams while eating delicious food? This
                                Mini-Masterclass will show you how.
                              </>
                            )}
                          </p>
                          <p>
                            <b>But here’s the deal:</b> It can’t wait. Every meal matters if you want to make a
                            difference.
                          </p>
                          <p>So, I’m going to share how to start seeing plant-based wins right away.</p>
                          <p>
                            After decades of inspiring people all over the world to eat food that gives them energy and
                            keeps them healthy, I can confidently say that when you eat more whole plant foods, the
                            rewards stack up.
                          </p>
                          <p>
                            Not just for you but for the planet, too. Small actions, especially what we eat, can make a
                            BIG difference.
                          </p>
                          <p>
                            {variant === 'ad' ? (
                              <>
                                In fact, eating the right foods can SLASH the risk of disease, and it can create more
                                energy, a sharper mind, and more hope for the future.
                              </>
                            ) : (
                              <>
                                In fact, eating the right foods can SLASH your risk of heart disease, Alzheimer’s,
                                cancer, Type 2 diabetes… and it can give you more energy, a sharper mind, more bounce in
                                your step, and more hope for your future.
                              </>
                            )}
                          </p>
                          <p>
                            If you’re willing to invest just 15 minutes, then I promise you’re going to be light-years
                            ahead of where you were when you woke up this morning.
                          </p>
                          <p>
                            My goal? After this free Mini-Masterclass, you’ll know what to do and how to do it. You’ll
                            be ready to step into your best health EVER. Deliciously.
                          </p>
                        </>
                      )
                    }[entry]
                  }
                </div>
              </Col>
            </Row>
          </Container>
        </Section>

        <Section id="landing-find-out" color="light-gray" angle="both">
          <Container>
            <Row>
              <Col>
                <h2 className="section-heading">Get ready to find out:</h2>
                {
                  {
                    curious: (
                      <IconList>
                        <IconListItem>
                          The results of the world’s most comprehensive long-term health study, tracking the diets,
                          lifestyle habits, and health outcomes of more than 96,000 people for four decades.{' '}
                          <b>Goodbye confusion, and hello clarity (and radiant health!).</b>
                        </IconListItem>
                        <IconListItem>
                          The stunning truth about your food and your planet. You won’t believe how powerful you are and
                          what a difference you can make for water, food security, and our climate.{' '}
                          <b>Goodbye, cynicism, and hello to making a difference on planet Earth!</b>
                        </IconListItem>
                        <IconListItem>
                          What the people with the longest (and healthiest) lifespans eat.{' '}
                          <b>Goodbye, fear of aging, and hello, longevity.</b>
                        </IconListItem>
                        <IconListItem>
                          The top 3 obstacles to going plant-based and what to do about them. In this Mini-Masterclass,
                          I’ll show you my BEST antidote to uninspired meals so you can excite your palate with meals
                          that you’ll always look forward to.{' '}
                          <b>Goodbye, boredom, and hello to loving foods that love you back!</b>
                        </IconListItem>
                        <IconListItem>
                          How to dodge the biggest plant-based diet mistake to get the MOST out of your meals.{' '}
                          <b>
                            Goodbye, fear of missing nutrients, and hello to giving your body what it needs to thrive.
                          </b>
                        </IconListItem>
                      </IconList>
                    ),
                    sustainability: (
                      <IconList>
                        <IconListItem>
                          The stunning truth about your food and your planet. You won’t believe how powerful you are and
                          what a difference you can make for water, food security, and our climate.{' '}
                          <b>
                            {variant === 'ad' ? (
                              <>Goodbye, cynicism, and hello to making a difference on planet Earth!</>
                            ) : (
                              <>Goodbye, cynicism, and hello, making a difference on planet Earth!</>
                            )}
                          </b>
                        </IconListItem>
                        <IconListItem>
                          {variant === 'ad' ? (
                            <>
                              The top 3 obstacles to going plant-based and what to do about them. In this
                              mini-masterclass, I’ll share my BEST antidote to uninspired meals so you can excite your
                              palate with meals to look forward to.{' '}
                              <b>Goodbye, boredom, and hello to loving foods that love you back!</b>
                            </>
                          ) : (
                            <>
                              The top 3 obstacles to going plant-based and what to do about them. In this
                              mini-masterclass, I’ll show you my BEST antidote to uninspired meals so you can excite
                              your palate with meals that you’ll always look forward to.{' '}
                              <b>Goodbye, boredom, and hello to loving foods that love you back!</b>
                            </>
                          )}
                        </IconListItem>
                        <IconListItem>
                          How to dodge the biggest plant-based diet mistake to get the MOST out of your meals.{' '}
                          <b>
                            Goodbye, fear of missing nutrients, and hello to giving your body what it needs to thrive.
                          </b>
                        </IconListItem>
                        <IconListItem>
                          The results of the world’s most comprehensive long-term health study, tracking the diets,
                          lifestyle habits, and health outcomes of more than 96,000 people for four decades.{' '}
                          <b>Goodbye confusion, and hello clarity (and radiant health!).</b>
                        </IconListItem>
                        <IconListItem>
                          How to eat for a healthy body and a healthy planet.{' '}
                          <b>{variant === 'ad' ? 'We' : 'You'} can have it all!</b>
                        </IconListItem>
                      </IconList>
                    ),
                    health: (
                      <IconList>
                        <IconListItem>
                          What the people with the longest (and healthiest) lifespans eat.{' '}
                          <b>Goodbye, fear of aging, and hello, longevity.</b>
                        </IconListItem>
                        <IconListItem>
                          The results of the world’s most comprehensive long-term health study, tracking the diets,
                          lifestyle habits, and health outcomes of more than 96,000 people for four decades.{' '}
                          <b>Goodbye confusion, and hello clarity (and radiant health!).</b>
                        </IconListItem>
                        <IconListItem>
                          {variant === 'ad' ? (
                            <>
                              The top 3 obstacles to going plant-based and what to do about them. In this
                              Mini-Masterclass, I’ll share my BEST antidote to uninspired meals so you can excite your
                              palate with meals to look forward to.{' '}
                              <b>Goodbye, boredom, and hello to loving foods that love you back</b>
                            </>
                          ) : (
                            <>
                              The top 3 obstacles to going plant-based and what to do about them. In this
                              Mini-Masterclass, I’ll show you my BEST antidote to uninspired meals so you can excite
                              your palate with meals that you’ll always look forward to.{' '}
                              <b>Goodbye, boredom, and hello to loving foods that love you back!</b>
                            </>
                          )}
                        </IconListItem>
                        <IconListItem>
                          How to dodge the biggest plant-based diet mistake to get the MOST out of your meals.{' '}
                          <b>
                            Goodbye, fear of missing nutrients, and hello to giving your body what it needs to thrive.
                          </b>
                        </IconListItem>
                        <IconListItem>
                          The stunning truth about your food and your planet. You won’t believe how powerful you are and
                          what a difference you can make for water, food security, and our climate.{' '}
                          <b>Goodbye, cynicism, and hello to making a difference on planet Earth!</b>
                        </IconListItem>
                      </IconList>
                    )
                  }[entry]
                }
                <h5>All in just 15 minutes!</h5>
              </Col>
            </Row>
          </Container>
        </Section>

        <SectionImage
          id="landing-cta"
          className="section-angle-last"
          color="purple"
          innerClass="text-white text-center"
          imgPosition="left"
          imgUrl={require('static/backgrounds/1357522236.jpg')}
        >
          {variant === 'ad' ? null : (
            <>{defaultSchedule.phase === 'during' ? <LandingTimer className="mt-4" light /> : null}</>
          )}
          <OptinButton className="my-4" />
          <p className="balance-text">This Mini-Masterclass is absolutely FREE and plays on demand.</p>
        </SectionImage>
      </div>

      <div className="footer-content mt-auto">
        <Footer />
        {noShare ? null : (
          <ShareButtons
            shareUrl={shareUrl}
            shareImg={ogImage}
            facebookDescription={facebookDescription}
            twitterDescription={twitterDescription}
            twitterImg={twitterImg}
            pinterestDescription={pinterestDescription}
            pinterestImage={pinterestImage}
            floating
          />
        )}
      </div>

      <OptinModal entry={entry} variant={variant} />
      {noExitIntent ? null : <ModalExitLanding />}
    </div>
  );
}
